import React from 'react';
import PropTypes from 'prop-types';

import {Alert, Box, CircularProgress} from 'views/components';
import {translate} from 'views/module';

import Interviews from 'views/scenes/caregiver/detail/caregiver_scenes/caregiver_feed/interviews/Interviews';

import backend from 'utils/backend';

// The caregiver's interviews, as on the legacy caregiver page. A job interview that completed an
// invitation (`invitation.interviewId`) gets that invitation, so its card plays the full recording
// that Loom stores. The other interviews keep the legacy recording button.
const LoomCaregiverInterviews = (props) => {
  const {caregiverId, caregiverInterviews, refresh} = props;

  const [invitations, setInvitations] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let isCancelled = false;

    const loadInvitations = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await backend.loom.caregiver.listInvitations(caregiverId);
        if (!isCancelled) setInvitations(result);
      } catch (loadError) {
        console.error(loadError);
        if (isCancelled) return;
        setInvitations([]);
        setError(translate('caregiver.LoomCaregiverDetail.Interviews.LoadError'));
      } finally {
        if (!isCancelled) setLoading(false);
      }
    };

    loadInvitations();

    return () => {
      isCancelled = true;
    };
  }, [caregiverId, refresh]);

  const interviews = React.useMemo(() => {
    const invitationsByInterview = new Map(
        invitations
            .filter((invitation) => invitation.interviewId)
            .map((invitation) => [invitation.interviewId, invitation]),
    );

    return caregiverInterviews.map((interview) => {
      const invitation = invitationsByInterview.get(interview.id);
      return invitation ? {...interview, loomInvitation: invitation} : interview;
    });
  }, [caregiverInterviews, invitations]);

  if (loading) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', py: 3}}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {
        error &&
        <Alert severity='warning' sx={{m: 2}}>{error}</Alert>
      }
      <Interviews caregiverInterviews={interviews} />
    </>
  );
};
LoomCaregiverInterviews.propTypes = {
  caregiverId: PropTypes.string.isRequired,
  caregiverInterviews: PropTypes.array.isRequired,
  refresh: PropTypes.number.isRequired,
};

export default React.memo(LoomCaregiverInterviews);
